import React, { useEffect, useRef, useState } from "react";
import type { BoardProps } from "../pages/room";
import { t } from "../i18n";
import { Button } from "../ui";

export type Frame = [number, number, number][];

/** Replays the server's simulated frames from the latest shot event. */
export function useReplay(events: { list: any[]; seq: number }) {
  const [frame, setFrame] = useState<Frame | null>(null);
  useEffect(() => {
    const shot = events.list.find((e: any) => e.type === "shot");
    if (!shot?.frames?.length) return;
    const frames: Frame[] = shot.frames;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return setFrame(null);
    let i = 0;
    let raf = 0;
    let last = 0;
    const step = (ts: number) => {
      if (ts - last > 1000 / 30) {
        last = ts;
        setFrame(frames[i]);
        i++;
      }
      if (i < frames.length) raf = requestAnimationFrame(step);
      else setFrame(null);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [events.seq]);
  return frame;
}

const COLORS: Record<number, string> = { 1: "#F7C531", 2: "#2F6FE4", 3: "#E2412F", 4: "#7A3FC4", 5: "#F28A1E", 6: "#1E9E5A", 7: "#8B2232", 8: "#111018" };
const W = 1000;
const H = 500;
const R = 11;
const POCKETS: [number, number][] = [[0, 0], [500, -6], [1000, 0], [0, 500], [500, 506], [1000, 500]];

export function PoolBoard({ view, seat, names, myTurn, move, events }: BoardProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const frame = useReplay(events);
  const [angle, setAngle] = useState(0);
  const [power, setPower] = useState(0.6);
  const animating = !!frame;

  const balls: { id: number; x: number; y: number }[] = frame
    ? frame.map(([id, x, y]) => ({ id, x, y }))
    : view.balls.filter((b: any) => !b.pocketed);
  const cue = balls.find((b) => b.id === 0);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ctx = c.getContext("2d")!;
    const dpr = window.devicePixelRatio || 1;
    const cw = c.clientWidth;
    c.width = cw * dpr;
    c.height = (cw / 2) * dpr;
    const s = (cw * dpr) / (W + 60);
    ctx.setTransform(s, 0, 0, s, 30 * s, 30 * s);
    ctx.fillStyle = "#3b2a1a";
    ctx.fillRect(-30, -30, W + 60, H + 60);
    ctx.fillStyle = "#0f6b55";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#0a0a12";
    for (const [x, y] of POCKETS) {
      ctx.beginPath();
      ctx.arc(x, y, 22, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = "rgba(255,255,255,.15)";
    ctx.beginPath();
    ctx.moveTo(250, 0);
    ctx.lineTo(250, H);
    ctx.stroke();
    for (const b of balls) {
      ctx.beginPath();
      ctx.arc(b.x, b.y, R, 0, Math.PI * 2);
      if (b.id === 0) ctx.fillStyle = "#FFF8EE";
      else ctx.fillStyle = b.id > 8 ? "#FFF8EE" : COLORS[b.id];
      ctx.fill();
      if (b.id > 8) {
        ctx.save();
        ctx.clip();
        ctx.fillStyle = COLORS[b.id - 8];
        ctx.fillRect(b.x - R, b.y - R * 0.55, R * 2, R * 1.1);
        ctx.restore();
      }
      if (b.id) {
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.arc(b.x, b.y, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#111";
        ctx.font = "bold 6px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(b.id), b.x, b.y + 0.5);
      }
    }
    if (myTurn && !animating && cue && !view.ballInHand) {
      ctx.strokeStyle = "rgba(255,244,224,.7)";
      ctx.setLineDash([8, 8]);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cue.x, cue.y);
      ctx.lineTo(cue.x + Math.cos(angle) * 700, cue.y + Math.sin(angle) * 700);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.strokeStyle = "#E7C48B";
      ctx.lineWidth = 6;
      ctx.beginPath();
      const back = 20 + power * 90;
      ctx.moveTo(cue.x - Math.cos(angle) * back, cue.y - Math.sin(angle) * back);
      ctx.lineTo(cue.x - Math.cos(angle) * (back + 320), cue.y - Math.sin(angle) * (back + 320));
      ctx.stroke();
    }
  });

  const toTable = (e: React.PointerEvent) => {
    const r = canvas.current!.getBoundingClientRect();
    const s = r.width / (W + 60);
    return [(e.clientX - r.left) / s - 30, (e.clientY - r.top) / s - 30];
  };
  const onPointer = (e: React.PointerEvent) => {
    if (!myTurn || animating) return;
    const [x, y] = toTable(e);
    if (view.ballInHand) {
      if (e.type === "pointerdown") move({ type: "place", x, y });
      return;
    }
    if (cue && (e.type === "pointerdown" || e.buttons)) setAngle(Math.atan2(y - cue.y, x - cue.x));
  };

  const group = view.groups[seat];
  return (
    <div className="pool">
      <div className="pool-status">
        {view.groups.map((g: string | null, s: number) => (
          <span key={s} className={s === view.turn ? "is-turn" : ""}>
            {names[s]}: {g === "solid" ? t.solids : g === "stripe" ? t.stripes : t.openTable}
          </span>
        ))}
        {view.lastFoul && <span className="foul">{t.foul}</span>}
      </div>
      <canvas
        ref={canvas}
        className="pool-table"
        onPointerDown={onPointer}
        onPointerMove={onPointer}
        style={{ touchAction: "none" }}
        aria-label={t.game.billiards}
      />
      <div className="board-controls">
        {myTurn && view.ballInHand && <span className="hint">{t.placeCue}</span>}
        {myTurn && !view.ballInHand && (
          <>
            <span className="hint">{t.aimHint}</span>
            <label className="power">
              {t.power}
              <input type="range" min="0.05" max="1" step="0.01" value={power} onChange={(e) => setPower(Number(e.target.value))} />
            </label>
            <Button disabled={animating} onClick={() => move({ type: "shoot", angle, power })}>{t.shoot}</Button>
          </>
        )}
        {!myTurn && <span className="hint">{t.turnOf(names[view.turn] ?? "")}</span>}
        {group && <span className="muted">{group === "solid" ? t.solids : t.stripes}</span>}
      </div>
    </div>
  );
}
